import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useSWR from 'swr';
import {
  ChevronDownIcon,
  ChevronRightIcon,
  CogIcon,
  ConfirmationDialog,
  FolderIcon,
  RefreshIcon,
  SearchIcon,
  Select,
  ShipIcon,
  TextInput,
} from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { fetcher } from '../../../services/fetcher';
import {
  deletePmsComponent,
  deletePmsDepartment,
  deletePmsEquipment,
  deletePmsEquipmentInstance,
  deletePmsEquipmentSystem,
  deletePmsMaintenanceJob,
  deletePmsSubComponent,
} from '../../../services/mutations';
import {
  buildPmsHierarchyTree,
  CHILD_TYPE_LABELS,
  coerceSelectionToTree,
  collectExpandKeys,
  detailPathFor,
  editPathFor,
  equipmentExtraChildTypes,
  expandedKeysForSelection,
  filterTreeByQuery,
  findNode,
  newPathFor,
  NODE_TYPE_LABELS,
  selectionMatches,
  selectionTitle,
  suggestedChildType,
  type PmsChildRow,
  type PmsHierarchyPayload,
  type PmsHierarchySelection,
  type PmsNodeType,
  type PmsTreeIcon,
  type PmsTreeNode,
} from './buildPmsHierarchyTree';
import {
  HierarchyChildrenInbox,
  type HierarchyCreateAction,
} from './HierarchyChildrenInbox';
import {
  clampTreeWidth,
  loadPmsHierarchyUiState,
  PMS_HIERARCHY_PATH,
  savePmsHierarchyUiState,
} from './pmsHierarchySession';

const TREE_WIDTH_DEFAULT = 320;

const CountPill: React.FC<{ count: number }> = ({ count }) => (
  <span className="ml-2 shrink-0 rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium tabular-nums text-slate-600 dark:bg-slate-800 dark:text-slate-300">
    {count}
  </span>
);

const treeIcon = (icon: PmsTreeIcon): React.ReactNode => {
  switch (icon) {
    case 'vessel':
      return <ShipIcon className="h-3.5 w-3.5" />;
    case 'department':
      return <FolderIcon className="h-3.5 w-3.5" />;
    case 'system':
      return <CogIcon className="h-3.5 w-3.5" />;
    case 'component':
    case 'subcomponent':
      return <FolderIcon className="h-3.5 w-3.5" />;
    case 'equipment':
      return <CogIcon className="h-3.5 w-3.5" />;
    case 'instance':
      return <CogIcon className="h-3.5 w-3.5" />;
    case 'job':
      return <FolderIcon className="h-3.5 w-3.5" />;
    default:
      return null;
  }
};

const TreeRow: React.FC<{
  indent: number;
  selected: boolean;
  expandable?: boolean;
  expanded?: boolean;
  onToggle?: () => void;
  onSelect: () => void;
  label: string;
  count: number;
  muted?: boolean;
  icon?: React.ReactNode;
  iconClassName?: string;
}> = ({
  indent,
  selected,
  expandable,
  expanded,
  onToggle,
  onSelect,
  label,
  count,
  muted,
  icon,
  iconClassName,
}) => (
  <div
    className={cn(
      'group flex items-center gap-0.5 pr-2 py-1 rounded-md cursor-pointer text-sm',
      selected
        ? 'bg-sky-100 text-sky-900 dark:bg-sky-950/60 dark:text-sky-100'
        : 'hover:bg-slate-100 dark:hover:bg-slate-800/80 text-slate-800 dark:text-slate-200',
    )}
    style={{ paddingLeft: 6 + indent * 12 }}
    onClick={onSelect}
    role="treeitem"
    aria-selected={selected}
  >
    {expandable ? (
      <button
        type="button"
        className={cn(
          'shrink-0 h-6 w-6 inline-flex items-center justify-center rounded',
          'text-slate-500 hover:bg-slate-200/80 dark:hover:bg-slate-700',
        )}
        aria-label={expanded ? 'Collapse' : 'Expand'}
        onClick={(e) => {
          e.stopPropagation();
          onToggle?.();
        }}
      >
        {expanded ? <ChevronDownIcon className="h-3.5 w-3.5" /> : <ChevronRightIcon className="h-3.5 w-3.5" />}
      </button>
    ) : (
      <span className="shrink-0 w-6" />
    )}
    {icon && (
      <span
        className={cn(
          'shrink-0 inline-flex items-center justify-center mr-2',
          selected ? 'text-sky-700 dark:text-sky-300' : iconClassName ?? 'text-slate-400 dark:text-slate-500',
        )}
        aria-hidden
      >
        {icon}
      </span>
    )}
    <span
      className={cn(
        'min-w-0 flex-1 truncate',
        muted && 'italic text-slate-500 dark:text-slate-400',
        selected && 'font-medium',
      )}
      title={label}
    >
      {label}
    </span>
    <CountPill count={count} />
  </div>
);

const TreeNodes: React.FC<{
  nodes: PmsTreeNode[];
  depth: number;
  selection: PmsHierarchySelection | null;
  onSelect: (sel: PmsHierarchySelection) => void;
  expanded: Set<string>;
  toggle: (key: string) => void;
  forceExpand: boolean;
}> = ({ nodes, depth, selection, onSelect, expanded, toggle, forceExpand }) => (
  <>
    {nodes.map((node) => {
      const hasChildren = Boolean(node.children?.length);
      const expKey = node.expandKey;
      const isExpanded = Boolean(expKey && (expanded.has(expKey) || forceExpand));
      return (
        <React.Fragment key={node.key}>
          <TreeRow
            indent={depth}
            selected={selectionMatches(selection, node.selection)}
            expandable={hasChildren}
            expanded={isExpanded}
            onToggle={expKey ? () => toggle(expKey) : undefined}
            onSelect={() => onSelect(node.selection)}
            label={node.label}
            count={node.count}
            muted={node.muted}
            icon={treeIcon(node.icon)}
            iconClassName={node.iconClassName}
          />
          {hasChildren && isExpanded && (
            <TreeNodes
              nodes={node.children!}
              depth={depth + 1}
              selection={selection}
              onSelect={onSelect}
              expanded={expanded}
              toggle={toggle}
              forceExpand={forceExpand}
            />
          )}
        </React.Fragment>
      );
    })}
  </>
);

const HierarchyTree: React.FC<{
  tree: PmsTreeNode[];
  selection: PmsHierarchySelection | null;
  onSelect: (sel: PmsHierarchySelection) => void;
  query: string;
  expanded: Set<string>;
  onExpandedChange: (next: Set<string>) => void;
}> = ({ tree, selection, onSelect, query, expanded, onExpandedChange }) => {
  useEffect(() => {
    const next = new Set(expanded);
    Array.from(expandedKeysForSelection(tree, selection)).forEach((k) => next.add(k));
    if (next.size !== expanded.size || Array.from(next).some((k) => !expanded.has(k))) {
      onExpandedChange(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to selection changes
  }, [selection]);

  const q = query.trim();
  const visible = useMemo(() => filterTreeByQuery(tree, q), [tree, q]);
  const forceExpand = Boolean(q);

  useEffect(() => {
    if (!forceExpand) return;
    const keys = collectExpandKeys(visible);
    onExpandedChange(new Set([...Array.from(expanded), ...Array.from(keys)]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceExpand, visible]);

  const toggle = (key: string) => {
    const next = new Set(expanded);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onExpandedChange(next);
  };

  if (!visible.length) {
    return (
      <div className="px-3 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
        {q ? 'No matches' : 'No hierarchy for this vessel yet'}
      </div>
    );
  }

  return (
    <div role="tree" className="py-1">
      <TreeNodes
        nodes={visible}
        depth={0}
        selection={selection}
        onSelect={onSelect}
        expanded={expanded}
        toggle={toggle}
        forceExpand={forceExpand}
      />
    </div>
  );
};

const DELETE_HANDLERS: Partial<Record<PmsNodeType, (id: number) => Promise<unknown>>> = {
  department: deletePmsDepartment,
  system: deletePmsEquipmentSystem,
  component: deletePmsComponent,
  subcomponent: deletePmsSubComponent,
  equipment: deletePmsEquipment,
  instance: deletePmsEquipmentInstance,
  job: deletePmsMaintenanceJob,
};

export const PmsHierarchyPage: React.FC = () => {
  const { colors } = useTheme();
  const saved = useMemo(() => loadPmsHierarchyUiState(), []);

  const [treeQuery, setTreeQuery] = useState(saved?.treeQuery ?? '');
  const [selection, setSelection] = useState<PmsHierarchySelection | null>(saved?.selection ?? null);
  const [expanded, setExpanded] = useState<Set<string>>(
    () => new Set(saved?.expandedKeys ?? []),
  );
  const [treeWidth, setTreeWidth] = useState(saved?.treeWidth ?? TREE_WIDTH_DEFAULT);
  const [treeResizing, setTreeResizing] = useState(false);
  const [vesselId, setVesselId] = useState<number | null>(saved?.vesselId ?? null);
  const [deleteTarget, setDeleteTarget] = useState<PmsChildRow | null>(null);
  const [deleting, setDeleting] = useState(false);
  const treeResizeStartRef = useRef<{ x: number; width: number } | null>(null);

  type VesselOptionRow = { id: number; vesselName: string; isActive?: boolean };
  const { data: vesselsRaw, isLoading: vesselsLoading } = useSWR<
    VesselOptionRow[] | { data?: VesselOptionRow[] }
  >('/vessels?page=1&limit=500', fetcher, { revalidateOnFocus: false });

  const allVessels = useMemo(() => {
    const list = Array.isArray(vesselsRaw) ? vesselsRaw : vesselsRaw?.data ?? [];
    return list.filter((v) => v.isActive !== false);
  }, [vesselsRaw]);

  const { data, error, isLoading, mutate, isValidating } = useSWR<PmsHierarchyPayload>(
    vesselId != null ? `/pms-hierarchy/tree?vesselId=${vesselId}` : null,
    fetcher,
    { revalidateOnFocus: true },
  );

  const fullTree = useMemo(
    () =>
      buildPmsHierarchyTree(
        data ?? {
          vessels: [],
          departments: [],
          systems: [],
          components: [],
          subComponents: [],
          equipment: [],
          equipmentInstances: [],
          maintenanceJobs: [],
        },
      ),
    [data],
  );

  const tree = useMemo(() => {
    if (vesselId == null) return [];
    return fullTree.filter(
      (n) => n.selection.type === 'vessel' && n.selection.id === vesselId,
    );
  }, [fullTree, vesselId]);

  const vesselOptions = useMemo(
    () =>
      allVessels.map((v) => ({
        value: String(v.id),
        label: v.vesselName,
      })),
    [allVessels],
  );

  // Vessel is required: restore saved choice or default to the first active vessel.
  useEffect(() => {
    if (!allVessels.length) return;
    const stillValid = vesselId != null && allVessels.some((v) => v.id === vesselId);
    if (stillValid) return;
    const nextId = allVessels[0]!.id;
    setVesselId(nextId);
    setSelection({ type: 'vessel', id: nextId });
  }, [allVessels, vesselId]);

  useEffect(() => {
    setSelection((prev) => coerceSelectionToTree(tree, prev));
  }, [tree]);

  const persistUiNow = useCallback(() => {
    savePmsHierarchyUiState({
      selection,
      expandedKeys: Array.from(expanded),
      treeQuery,
      treeWidth,
      vesselId,
    });
  }, [selection, expanded, treeQuery, treeWidth, vesselId]);

  useEffect(() => {
    persistUiNow();
  }, [persistUiNow]);

  useEffect(() => {
    if (!treeResizing) return;
    const onMove = (e: MouseEvent) => {
      const start = treeResizeStartRef.current;
      if (!start) return;
      setTreeWidth(clampTreeWidth(start.width + (e.clientX - start.x)));
    };
    const onUp = () => {
      treeResizeStartRef.current = null;
      setTreeResizing(false);
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [treeResizing]);

  const selectedNode = useMemo(() => findNode(tree, selection), [tree, selection]);
  const pathTitle = useMemo(() => selectionTitle(tree, selection), [tree, selection]);
  const shortTitle = selectedNode?.label ?? 'Select a node';
  const childRows = selectedNode?.childRows ?? [];
  const childType = suggestedChildType(selection);

  const vesselIdForNew = useMemo(() => {
    if (!selection) return undefined;
    if (selection.type === 'vessel') return selection.id;
    const walk = (nodes: PmsTreeNode[], vesselId?: number): number | undefined => {
      for (const n of nodes) {
        const vid = n.selection.type === 'vessel' ? n.selection.id : vesselId;
        if (selectionMatches(selection, n.selection)) return vid;
        if (n.children) {
          const found = walk(n.children, vid);
          if (found != null) return found;
        }
      }
      return undefined;
    };
    return walk(tree);
  }, [selection, tree]);

  const createActions = useMemo((): HierarchyCreateAction[] => {
    if (!selection || !childType) return [];
    const primaryHref = newPathFor(childType, selection, vesselIdForNew);
    const actions: HierarchyCreateAction[] = [
      {
        type: childType,
        href: primaryHref,
        label: CHILD_TYPE_LABELS[childType],
        hint:
          selection.type === 'equipment'
            ? 'Physical serial for this equipment slot'
            : `Create under ${NODE_TYPE_LABELS[selection.type].toLowerCase()}`,
        primary: true,
      },
    ];
    if (selection.type === 'equipment') {
      for (const type of equipmentExtraChildTypes()) {
        actions.push({
          type,
          href: newPathFor(type, selection, vesselIdForNew),
          label: CHILD_TYPE_LABELS[type],
          hint:
            type === 'job'
              ? 'Planned maintenance job for this equipment'
              : 'Nested equipment under this slot',
          primary: false,
        });
      }
    }
    return actions;
  }, [selection, childType, vesselIdForNew]);

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    const handler = DELETE_HANDLERS[deleteTarget.type];
    if (!handler) {
      setDeleteTarget(null);
      return;
    }
    setDeleting(true);
    try {
      await handler(deleteTarget.id);
      setDeleteTarget(null);
      await mutate();
    } catch {
      // keep dialog open; user can cancel
    } finally {
      setDeleting(false);
    }
  }, [deleteTarget, mutate]);

  const openSelectedHref = selection ? detailPathFor(selection.type, selection.id) : null;
  const editSelectedHref =
    selection && selection.type !== 'vessel' ? editPathFor(selection.type, selection.id) : null;

  const ghostBtn = cn(
    'inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border text-sm transition-colors',
    colors.surface,
    colors.text,
    colors.border,
    colors.interactiveHover,
  );

  return (
    <div className="flex flex-col h-full min-h-0">
      <div
        className={cn(
          'flex-shrink-0 mb-2 flex flex-wrap items-center gap-3 rounded-xl border px-3 py-2.5 shadow-sm',
          'bg-white dark:bg-slate-950',
          colors.border,
        )}
      >
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <div className="flex min-w-[14rem] max-w-sm flex-1 items-center gap-2 sm:min-w-[16rem]">
            <span
              className={cn(
                'inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border',
                colors.border,
                'bg-slate-50 text-slate-600 dark:bg-slate-900 dark:text-slate-300',
              )}
              aria-hidden
            >
              <ShipIcon className="h-4 w-4" />
            </span>
            <div className="min-w-0 flex-1">
              <Select
                value={vesselId != null ? String(vesselId) : ''}
                onChange={(e) => {
                  const next = Number(e.target.value);
                  if (!Number.isFinite(next) || next <= 0) return;
                  setVesselId(next);
                  setSelection({ type: 'vessel', id: next });
                  setExpanded((prev) => new Set([...Array.from(prev), `vessel:${next}`]));
                }}
                options={vesselOptions}
                placeholder={vesselsLoading || isLoading ? 'Loading vessels…' : 'Select vessel'}
                required
                showClearButton={false}
                className="h-9 text-sm"
                aria-label="Vessel"
              />
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => {
            void mutate();
          }}
          disabled={isValidating}
          className={cn(ghostBtn, 'w-9 px-0 justify-center ml-auto', isValidating && 'opacity-60')}
          title="Refresh"
          aria-label="Refresh"
        >
          <RefreshIcon className={cn('w-5 h-5', isValidating && 'animate-spin')} />
        </button>
      </div>

      <div
        className={cn(
          'flex flex-col flex-1 min-h-0 overflow-hidden rounded-xl border shadow-sm',
          'bg-white dark:bg-slate-950',
          colors.border,
        )}
      >
        <div
          className="flex-1 min-h-0 flex flex-col lg:flex-row"
          style={{ ['--pc-tree-w' as string]: `${treeWidth}px` }}
        >
          <aside
            className={cn(
              'flex flex-col min-h-0 border-b lg:border-b-0 w-full max-h-64 lg:max-h-none lg:shrink-0 lg:w-[var(--pc-tree-w)]',
              colors.border,
            )}
          >
            <div className="flex-shrink-0 p-2 border-b border-slate-100 dark:border-slate-800">
              <div className="relative flex-1 min-w-0">
                <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                <TextInput
                  value={treeQuery}
                  onChange={(e) => setTreeQuery(e.target.value)}
                  placeholder="Filter tree…"
                  className="pl-8 h-9 text-sm"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto dashboard-scrollbar px-1">
              {isLoading ? (
                <div className="px-3 py-8 text-center text-sm text-slate-500">Loading…</div>
              ) : error ? (
                <div className="px-3 py-8 text-center text-sm text-rose-600">Could not load hierarchy</div>
              ) : vesselId == null ? (
                <div className="px-3 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
                  Select a vessel to view the hierarchy
                </div>
              ) : (
                <HierarchyTree
                  tree={tree}
                  selection={selection}
                  onSelect={setSelection}
                  query={treeQuery}
                  expanded={expanded}
                  onExpandedChange={setExpanded}
                />
              )}
            </div>
          </aside>

          <div
            role="separator"
            aria-orientation="vertical"
            aria-valuenow={treeWidth}
            aria-label="Resize tree panel"
            title="Drag to resize"
            onMouseDown={(e) => {
              if (e.button !== 0) return;
              e.preventDefault();
              treeResizeStartRef.current = { x: e.clientX, width: treeWidth };
              setTreeResizing(true);
            }}
            className={cn(
              'hidden lg:flex shrink-0 w-1.5 cursor-col-resize items-center justify-center group',
              'hover:bg-sky-500/20 active:bg-sky-500/30',
              'border-l',
              colors.border,
              treeResizing && 'bg-sky-500/30',
            )}
          >
            <div className="w-0.5 h-12 rounded-full bg-slate-400 dark:bg-slate-500 opacity-60 group-hover:opacity-100" />
          </div>

          <section className="flex min-h-0 min-w-0 flex-1 flex-col p-2 sm:p-3">
            {!selection ? (
              <div className="flex flex-1 items-center justify-center rounded-2xl border border-dashed border-slate-200 bg-white p-6 dark:border-slate-700 dark:bg-slate-900/50">
                <div className="max-w-sm w-full px-6 py-12 text-center">
                  <span className="mx-auto mb-3 inline-flex h-11 w-11 items-center justify-center rounded-xl bg-slate-100 text-slate-500 dark:bg-slate-800">
                    <FolderIcon className="h-5 w-5" />
                  </span>
                  <p className="text-sm font-medium text-slate-800 dark:text-slate-100">Select a node</p>
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    Pick a vessel or hierarchy item on the left to view children and add records.
                  </p>
                </div>
              </div>
            ) : (
              <HierarchyChildrenInbox
                title={shortTitle}
                description={pathTitle !== shortTitle ? pathTitle : undefined}
                selectionType={selection.type}
                childRows={childRows}
                createActions={createActions}
                openSelectedHref={openSelectedHref}
                editSelectedHref={editSelectedHref}
                isLoading={isLoading}
                onPersistUi={persistUiNow}
                onDelete={setDeleteTarget}
                canDeleteType={(type) => Boolean(DELETE_HANDLERS[type])}
              />
            )}
          </section>
        </div>
      </div>

      <ConfirmationDialog
        isOpen={!!deleteTarget}
        onClose={() => !deleting && setDeleteTarget(null)}
        onConfirm={() => {
          void handleConfirmDelete();
        }}
        title="Delete"
        message={
          deleteTarget
            ? `Delete “${deleteTarget.name}” (${NODE_TYPE_LABELS[deleteTarget.type]})?`
            : 'Delete this record?'
        }
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
        isLoading={deleting}
      />
    </div>
  );
};

export default PmsHierarchyPage;
